"use client";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { FeatureItem } from "@components/atoms/FeatureItem";
import { createVariantSpecificationsFromApi } from "@factories/variantSpecificationsFactory";
import { fetchVariantSpecifications } from "@services/variantSpecificationsService";
import type { VariantComparisonResponse } from "@services/variantSpecificationsService";
import { fetchModelDetailsVariants } from "@services/queries/findModelDetailsVariants";
import { cn } from "@utils/utils";
import { CategoryTabs } from "./CategoryTabs";
import styles from "./SpecificationSection.module.scss";
import { VariantSelector } from "./VariantSelector";
import { Loading } from "./Loading";
import ChatbotSupport from "@components/molecules/ChatbotSupport";
import { DEFAULT_CITY } from "@constants/global";

interface AllVariantSlugs {
  uniqueSortedVariants?: {
    variantName: string;
    slug: string;
  }[];
}

interface DetailedVariant {
  variantName: string;
  fuelType: string;
  engine: string;
  emission: string;
  transmission: string;
  slug: string;
  marketPrice: number;
  adspPrice: number;
  ackoDriveSavings: number;
  exShowRoomPrice: number;
  onRoadPrice: number;
  mileage: {
    value: number;
    unit: string;
  };
  variantLevel: string;
  expressDelivery: boolean;
  expressDeliveryDays: number;
  standardDeliveryDays: number;
  status: string;
}

interface SpecificationsSectionClientWrapperProps {
  variantSlug: string;
  allVariantSlugs: AllVariantSlugs;
  selectedVariantId?: string;
  selectedCategoryIndex?: number;
  className?: string;
  carName?: string;
  showVariantDropdown?: boolean;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  isSellable?: boolean;
  pageType?: string;
  carAvailability?: string;
  isLoggedIn?: boolean;
  cityName?: string;
  currentVariantSlug: string;
  allAvailableVariants: any[];
  selectedVariant: any;
  isUpsellFlow?: boolean;
  initialApiResponse?: VariantComparisonResponse | null;
  initialDetailedVariants?: DetailedVariant[];
}

export default function SpecificationsSectionClientWrapper({
  variantSlug,
  selectedCategoryIndex = 0,
  className,
  carName,
  showVariantDropdown = true,
  brandName,
  modelName,
  variantName,
  pageType,
  isSellable,
  carAvailability,
  isLoggedIn,
  cityName,
  currentVariantSlug,
  allAvailableVariants,
  selectedVariant,
  isUpsellFlow,
  initialApiResponse = null,
  initialDetailedVariants = [],
}: SpecificationsSectionClientWrapperProps) {
  const searchParams = useSearchParams();
  const [apiResponse, setApiResponse] = useState<VariantComparisonResponse | null>(
    initialApiResponse
  );
  const [loading, setLoading] = useState<boolean>(!initialApiResponse);
  const [detailedVariants, setDetailedVariants] = useState<DetailedVariant[]>(
    initialDetailedVariants
  );
  const [activeVariantId, setActiveVariantId] = useState(
    selectedVariant?.id || currentVariantSlug
  );
  const [isChangingVariant, setIsChangingVariant] = useState(false);
  const [variantChangeError, setVariantChangeError] = useState("");
  const variantRequestId = React.useRef(0);
  const activeSpecificationsSlug = React.useRef(currentVariantSlug);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(
    selectedCategoryIndex
  );
  const categoryParam = searchParams?.get("category");
  const variantParam = searchParams?.get("variant");
  const activeVariant = allAvailableVariants.find(
    (variant) => variant.id === activeVariantId
  );
  const vehicleTitle = [
    brandName,
    modelName,
    activeVariant?.name || variantName,
  ]
    .filter(Boolean)
    .join(" ");
  const sectionTitle = isUpsellFlow
    ? "All Specifications"
    : vehicleTitle
      ? `${vehicleTitle} All Specifications`
      : "All specifications";
  const title = <h2 className={styles.specs__title}>{sectionTitle}</h2>;

  useEffect(() => {
    const targetSlug = variantParam || currentVariantSlug;
    const targetVariant = allAvailableVariants.find(
      (variant) => variant.id === targetSlug
    );

    if (!targetVariant || activeSpecificationsSlug.current === targetSlug) return;

    const requestId = ++variantRequestId.current;
    setIsChangingVariant(true);
    setVariantChangeError("");

    const loadSpecifications = async () => {
      try {
        const response = await fetchVariantSpecifications(targetSlug);
        if (requestId !== variantRequestId.current) return;
        if (!response?.current) {
          setVariantChangeError("Could not load specifications. Please try again.");
          return;
        }

        setApiResponse(response);
        setActiveVariantId(targetVariant.id);
        activeSpecificationsSlug.current = targetSlug;
      } catch (error) {
        if (requestId === variantRequestId.current) {
          console.error("Error loading variant specifications:", error);
          setVariantChangeError("Could not load specifications. Please try again.");
        }
      } finally {
        if (requestId === variantRequestId.current) setIsChangingVariant(false);
      }
    };

    void loadSpecifications();
  }, [variantParam, currentVariantSlug, allAvailableVariants]);

  const handleVariantSelect = async (variant: { id?: string; slug?: string }) => {
    if (!variant.slug) return;
    const requestId = ++variantRequestId.current;
    setIsChangingVariant(true);
    setVariantChangeError("");

    try {
      const response = await fetchVariantSpecifications(variant.slug);
      if (requestId !== variantRequestId.current) return;
      if (!response?.current) {
        setVariantChangeError("Could not load specifications. Please try again.");
        return;
      }

      setApiResponse(response);
      setActiveVariantId(variant.id || variant.slug);
      activeSpecificationsSlug.current = variant.slug;
      setActiveCategoryIndex(0);

      const params = new URLSearchParams(searchParams?.toString());
      params.set("variant", variant.slug);
      params.set("category", "0");
      const query = params.toString();
      window.history.pushState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
      );
    } catch (error) {
      if (requestId === variantRequestId.current) {
        console.error("Error loading variant specifications:", error);
        setVariantChangeError("Could not load specifications. Please try again.");
      }
    } finally {
      if (requestId === variantRequestId.current) setIsChangingVariant(false);
    }
  };

  useEffect(() => {
    const parsedCategoryIndex =
      categoryParam !== null && categoryParam !== undefined
        ? Number(categoryParam)
        : selectedCategoryIndex;

    if (Number.isFinite(parsedCategoryIndex) && parsedCategoryIndex >= 0) {
      setActiveCategoryIndex(parsedCategoryIndex);
    }
  }, [categoryParam, selectedCategoryIndex]);

  useEffect(() => {
    if (initialApiResponse) {
      setApiResponse(initialApiResponse);
      setDetailedVariants(initialDetailedVariants);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const [specificationsResponse, detailedVariantsResponse] =
          await Promise.all([
            fetchVariantSpecifications(currentVariantSlug),
            fetchModelDetailsVariants(variantSlug, cityName || DEFAULT_CITY),
          ]);

        if (isMounted) {
          setApiResponse(specificationsResponse);
          setDetailedVariants(
            detailedVariantsResponse?.allVariantSlugs?.uniqueSortedVariants ||
              []
          );
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setApiResponse(null);
          setDetailedVariants([]);
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    currentVariantSlug,
    variantSlug,
    cityName,
    initialApiResponse,
    initialDetailedVariants,
  ]);

  if (loading) {
    return (
      <div className={cn(styles.specs, className)}>
        {title}
        <Loading />
      </div>
    );
  }

  if (!apiResponse) {
    return (
      <div className={cn(styles.specs, className)}>
        {title}
        <div className={styles.specs__error}>Failed to load specifications</div>
      </div>
    );
  }

  const specificationsVariants =
    createVariantSpecificationsFromApi(apiResponse);

  if (allAvailableVariants.length === 0) {
    return (
      <div className={cn(styles.specs, className)}>
        {title}
        <div className={styles.specs__empty}>
          No variants available for this model.
        </div>
      </div>
    );
  }

  const currentVariant =
    specificationsVariants.length > 0 ? specificationsVariants[0] : null;

  if (
    !currentVariant ||
    (currentVariant && currentVariant.categories.length === 0)
  ) {
    return (
      <div className={cn(styles.specs, className)}>
        {title}
        <div className={styles.specs__empty}>
          No specifications available for this variant.
        </div>
      </div>
    );
  }

  const currentCategories = currentVariant.categories || [];
  const categoryIndex =
    Number.isFinite(activeCategoryIndex) && activeCategoryIndex >= 0
      ? Math.min(activeCategoryIndex, currentCategories.length - 1)
      : 0;

  return (
    <>
      {title}
      {showVariantDropdown && (
        <VariantSelector
          variants={allAvailableVariants}
          selectedVariantId={activeVariantId}
          detailedVariants={detailedVariants}
          brandName={brandName}
          modelName={modelName}
          variantName={variantName}
          isSellable={isSellable}
          pageType={pageType}
          carAvailability={carAvailability}
          isLoggedIn={isLoggedIn}
          cityName={cityName}
          onVariantSelect={handleVariantSelect}
          isChangingVariant={isChangingVariant}
        />
      )}
      {variantChangeError && (
      <div className={styles.specs__error} role="alert">
          {variantChangeError}
        </div>
      )}
      <div className={styles.specs__contentContainer}>
        <div className={styles.specs__content}>
          <CategoryTabs
            categories={currentCategories}
            selectedCategoryIndex={categoryIndex}
            selectedVariantId={activeVariantId}
            brandName={brandName}
            modelName={modelName}
            variantName={activeVariant?.name || variantName}
            pageType={pageType}
            carAvailability={carAvailability}
            onCategoryChange={setActiveCategoryIndex}
          />

          <div className={styles.specs__tabContent}>
            {currentCategories.map((category, index) => {
              const includedFeatures = category.features.filter(
                (feature) => feature.included
              );
              const missingFeatures = category.features.filter(
                (feature) => !feature.included
              );
              const isActive = index === categoryIndex;

              return (
                <div
                  key={category.title}
                  hidden={!isActive}
                  aria-hidden={!isActive}
                  data-category-index={index}
                  className={styles.specs__features}
                >
                  {includedFeatures.length > 0 && (
                    <div className={styles.specs__featureColumn}>
                      <h3 className={styles.specs__featureTitle}>
                        What&apos;s included
                      </h3>
                      <div className={styles.specs__featureList}>
                        {includedFeatures.map((feature, featureIndex) => (
                          <FeatureItem
                            key={`included-${category.title}-${feature.title}-${featureIndex}`}
                            {...feature}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {missingFeatures.length > 0 && (
                    <div className={styles.specs__featureColumn}>
                      <h3 className={styles.specs__featureTitle}>
                        Features you&apos;ll miss
                      </h3>
                      <div className={styles.specs__featureList}>
                        {missingFeatures.map((feature, featureIndex) => (
                          <FeatureItem
                            key={`missing-${category.title}-${feature.title}-${featureIndex}`}
                            {...feature}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {
          !isUpsellFlow &&
          <div className={styles.specs__chatbotContainer}>
            <ChatbotSupport
              heading={"Not sure which variant suits you?"}
              subheading="No worries, we'll help you with your question"
              buttonText={"Find the right variant"}
              buttonLink={""}
              icon={`/icons/chatbot.svg`}
              customChatbotStyling={styles.customChatbotStyling}
              chatBotPrompt={`Which variant should I buy in ${carName}?`}
              brandName={brandName}
              modelName={modelName}
              variantName={activeVariant?.name || variantName}
              carAvailability={carAvailability}
              pageType={pageType}
              section="Right variant"
              cityName={cityName}
            />
          </div>
        }
      </div>
    </>
  );
}
